import { useMemo } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { media } from '../../styles/breakpoints';
import {
  sidebarLink,
  sidebarLinkActive,
  sidebarChildLink,
  sidebarChildLinkActive,
  sidebarHeaderStyle,
  sidebarBadge,
} from '../../styles/sidebarStyles';

export interface SidebarItem {
  label: string;
  href?: string;
  badge?: string;
  expandBehavior?: 'always' | 'when-active';
  children?: SidebarChildItem[];
  /** Additional path prefixes that trigger this group's active state (not rendered in sidebar) */
  activePaths?: string[];
  /** Key into sidebar_permissions hash — item hidden when permission is false */
  permissionKey?: string;
}

export interface SidebarChildItem {
  label: string;
  href: string;
  badge?: string;
  /** Key into sidebar_permissions hash — child hidden when permission is false */
  permissionKey?: string;
}

interface PublicationSidebarProps {
  items: SidebarItem[];
  publicationId: string;
  sidebarPermissions?: Record<string, boolean>;
}

const SidebarContainer = styled.nav`
  width: 232px;
  min-width: 232px;
  padding: 16px 0;
  border-right: 1px solid #e0e0e0;
  background: #fafafa;
  min-height: calc(100vh - 40px);

  ${media.sidenav} {
    width: 100%;
    min-width: unset;
    min-height: auto;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
`;

const SidebarLink = styled.a<{ $active?: boolean }>`
  ${props => (props.$active ? sidebarLinkActive : sidebarLink)}
`;

const SidebarHeader = styled.div`
  ${sidebarHeaderStyle}
`;

const ChildLink = styled.a<{ $active?: boolean }>`
  ${props => (props.$active ? sidebarChildLinkActive : sidebarChildLink)}
`;

const Badge = styled.span`
  ${sidebarBadge}
`;

function resolveHref(href: string, publicationId: string): string {
  return href.replace(':id', publicationId);
}

function isPathActive(currentPath: string, href: string, prefixMatch = false): boolean {
  // Normalize trailing slashes for comparison
  const normalize = (p: string) => p.replace(/\/+$/, '');
  const normalizedCurrent = normalize(currentPath);
  const normalizedHref = normalize(href);
  if (normalizedCurrent === normalizedHref) return true;
  if (prefixMatch) {
    return normalizedCurrent.startsWith(normalizedHref + '/');
  }
  return false;
}

function isGroupActive(
  currentPath: string,
  item: SidebarItem,
  publicationId: string
): boolean {
  if (item.href && isPathActive(currentPath, resolveHref(item.href, publicationId), true)) {
    return true;
  }
  if (item.children) {
    if (item.children.some(child =>
      isPathActive(currentPath, resolveHref(child.href, publicationId), true)
    )) return true;
  }
  if (item.activePaths) {
    if (item.activePaths.some(p =>
      isPathActive(currentPath, resolveHref(p, publicationId), true)
    )) return true;
  }
  return false;
}

function isPermitted(
  permissionKey: string | undefined,
  permissions: Record<string, boolean> | undefined
): boolean {
  if (!permissionKey) return true;
  if (!permissions) return false;
  return permissions[permissionKey] !== false;
}

export default function PublicationSidebar({
  items,
  publicationId,
  sidebarPermissions,
}: PublicationSidebarProps) {
  const location = useLocation();
  const currentPath = location.pathname;

  const permittedItems = useMemo(
    () =>
      items
        .filter(item => isPermitted(item.permissionKey, sidebarPermissions))
        .map(item => {
          if (!item.children) return item;
          const filteredChildren = item.children.filter(child =>
            isPermitted(child.permissionKey, sidebarPermissions)
          );
          return { ...item, children: filteredChildren };
        })
        .filter(item => {
          // Remove groups that have no visible children after filtering
          if (item.expandBehavior === 'always' && item.children?.length === 0) return false;
          return true;
        }),
    [items, sidebarPermissions]
  );

  const renderedItems = useMemo(
    () =>
      permittedItems.map(item => {
        const hasChildren = item.children && item.children.length > 0;
        const expandAlways = item.expandBehavior === 'always';
        const expandWhenActive = item.expandBehavior === 'when-active';
        const groupActive = hasChildren
          ? isGroupActive(currentPath, item, publicationId)
          : false;
        const showChildren =
          hasChildren && (expandAlways || (expandWhenActive && groupActive));

        // Items with expandBehavior='always' are headers, not links
        if (expandAlways && hasChildren) {
          return (
            <div key={item.label} data-testid={`sidebar-group-${item.label}`}>
              <SidebarHeader>{item.label}</SidebarHeader>
              {item.children!.map(child => {
                const childHref = resolveHref(child.href, publicationId);
                const childActive = isPathActive(currentPath, childHref);
                return (
                  <ChildLink
                    key={child.href}
                    href={childHref}
                    $active={childActive}
                    aria-current={childActive ? 'page' : undefined}
                  >
                    <span>{child.label}</span>
                    {child.badge && <Badge>{child.badge}</Badge>}
                  </ChildLink>
                );
              })}
            </div>
          );
        }

        // Items with children that expand when active
        if (hasChildren) {
          const parentHref = item.href
            ? resolveHref(item.href, publicationId)
            : undefined;
          const parentActive = parentHref
            ? isPathActive(currentPath, parentHref)
            : groupActive;

          return (
            <div key={item.label} data-testid={`sidebar-group-${item.label}`}>
              {parentHref ? (
                <SidebarLink
                  href={parentHref}
                  $active={parentActive}
                  aria-current={parentActive ? 'page' : undefined}
                >
                  <span>{item.label}</span>
                  {item.badge && <Badge>{item.badge}</Badge>}
                </SidebarLink>
              ) : (
                <SidebarLink
                  as="div"
                  $active={groupActive}
                >
                  <span>{item.label}</span>
                  {item.badge && <Badge>{item.badge}</Badge>}
                </SidebarLink>
              )}
              {showChildren &&
                item.children!.map(child => {
                  const childHref = resolveHref(child.href, publicationId);
                  const childActive = isPathActive(currentPath, childHref);
                  return (
                    <ChildLink
                      key={child.href}
                      href={childHref}
                      $active={childActive}
                      aria-current={childActive ? 'page' : undefined}
                    >
                      <span>{child.label}</span>
                      {child.badge && <Badge>{child.badge}</Badge>}
                    </ChildLink>
                  );
                })}
            </div>
          );
        }

        // Simple leaf items (no children)
        if (!item.href) return null;
        const href = resolveHref(item.href, publicationId);
        const active = isPathActive(currentPath, href);
        return (
          <SidebarLink
            key={item.href}
            href={href}
            $active={active}
            aria-current={active ? 'page' : undefined}
          >
            <span>{item.label}</span>
            {item.badge && <Badge>{item.badge}</Badge>}
          </SidebarLink>
        );
      }),
    [permittedItems, currentPath, publicationId]
  );

  return (
    <SidebarContainer data-testid="publication-sidebar">
      {renderedItems}
    </SidebarContainer>
  );
}
